/**
 * Socket.IO Service
 * Real-time WebSocket connection for notifications
 * Matching mobile's implementation
 */

import { io, Socket } from 'socket.io-client';
import { getAccessToken } from '@/shared/utils/supabaseStorage';

const SOCKET_URL = import.meta.env.VITE_SIGNALING_SERVER_URL ?? 'https://webrtc.wlkthru.co';

class SocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<(data: any) => void>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  /**
   * Connect to Socket.IO server with auth token
   */
  async connect(): Promise<void> {
    if (this.socket?.connected) {
      console.log('[SocketService] Already connected');
      return;
    }

    try {
      // CRITICAL FIX: Get token from localStorage using shared utility
      const token = getAccessToken();

      if (!token) {
        console.warn('[SocketService] No auth token, cannot connect');
        return;
      }

      console.log('[SocketService] Connecting to:', SOCKET_URL);

      this.socket = io(SOCKET_URL, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: this.maxReconnectAttempts,
      });

      this.setupEventListeners();

    } catch (error) {
      console.error('[SocketService] Connection error:', error);
    }
  }

  /**
   * Set up Socket.IO event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', async () => {
      console.log('[SocketService] Connected:', this.socket?.id);
      this.reconnectAttempts = 0;

      // Refresh auth token on reconnect
      try {
        // CRITICAL FIX: Get token from localStorage using shared utility
        const token = getAccessToken();
        if (token) {
          this.socket!.auth = { token };
          console.log('[SocketService] Auth token refreshed on connect');
        }
      } catch (error) {
        console.error('[SocketService] Failed to refresh token on connect:', error);
      }

      this.reattachListeners();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[SocketService] Disconnected:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('[SocketService] Connection error:', error.message);
      this.reconnectAttempts++;
    });

    this.socket.on('reconnect', async (attemptNumber) => {
      console.log('[SocketService] Reconnected after', attemptNumber, 'attempts');

      // Refresh auth token after reconnect
      try {
        // CRITICAL FIX: Get token from localStorage using shared utility
        const token = getAccessToken();
        if (token) {
          this.socket!.auth = { token };
          console.log('[SocketService] Auth token refreshed after reconnect');
        }
      } catch (error) {
        console.error('[SocketService] Failed to refresh token after reconnect:', error);
      }
    });

    this.socket.on('reconnect_failed', () => {
      console.error('[SocketService] Reconnection failed after max attempts');
    });
  }

  /**
   * Reattach all listeners after reconnection
   */
  private reattachListeners(): void {
    if (!this.socket) return;

    console.log('[SocketService] Reattaching', this.listeners.size, 'listeners');

    this.listeners.forEach((callbacks, event) => {
      callbacks.forEach((callback) => {
        // Remove old listener before adding new one to prevent duplicates
        this.socket?.off(event, callback);
        this.socket?.on(event, callback);
      });
    });
  }

  /**
   * Add event listener
   */
  on(event: string, callback: (data: any) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event)!.add(callback);

    if (this.socket?.connected) {
      this.socket.on(event, callback);
    }
  }

  /**
   * Remove event listener
   */
  off(event: string, callback: (data: any) => void): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.listeners.delete(event);
      }
    }

    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  /**
   * Emit event to server
   */
  emit(event: string, data?: any): void {
    if (!this.socket?.connected) {
      console.warn('[SocketService] Not connected, cannot emit:', event);
      return;
    }

    this.socket.emit(event, data);
  }

  /**
   * Disconnect from server
   */
  disconnect(): void {
    if (this.socket) {
      console.log('[SocketService] Disconnecting');
      this.socket.disconnect();
      this.socket = null;
      this.listeners.clear();
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get socket instance
   */
  getSocket(): Socket | null {
    return this.socket;
  }
}

// Singleton instance
const socketService = new SocketService();

export default socketService;
